import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card } from "../components/ui";
import {
  api,
  type LastMileImportDeleteResult,
  type LastMileImportResult,
  type LastMileImportSummary,
} from "../data/api";
import {
  parseRepartoExcel,
  statsFor,
  type ParseError,
  type RepartoRow,
  type RepartoStats,
} from "../features/lastmile/repartoExcelParser";

// LastMileImportPage — carga un Excel de planificación (formato Dimexa
// Reparto_*.xlsx) y lo importa a la BD vía POST /api/last-mile/import.
//
// Flujo UX:
//   1. Drag & drop o file picker del .xlsx
//   2. Parseo local con SheetJS — preview de stats (totales, choferes,
//      fechas, estados)
//   3. Botón "Importar" → llama al backend, muestra el resultado
//
// El parseo es 100% en el browser; el backend recibe filas tipadas via
// JSON (~2-5 MB para 2000 filas). No subimos el .xlsx al server.

type Stage = "idle" | "parsing" | "preview" | "uploading" | "done";

export function LastMileImportPage() {
  const [stage, setStage] = useState<Stage>("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<RepartoRow[]>([]);
  const [stats, setStats] = useState<RepartoStats | null>(null);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LastMileImportResult | null>(null);

  // Drag state (sólo para el highlight visual).
  const [isDragging, setIsDragging] = useState(false);

  const onSelectFile = useCallback(async (file: File) => {
    setError(null);
    setResult(null);
    setStage("parsing");
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const result = parseRepartoExcel(buf);
      if (!result.ok) {
        setError(describeParseError(result.error));
        setStage("idle");
        return;
      }
      setRows(result.rows);
      setStats(statsFor(result.rows));
      setParseWarnings(result.warnings);
      setStage("preview");
    } catch (e) {
      setError(`Error leyendo Excel: ${(e as Error).message}`);
      setStage("idle");
    }
  }, []);

  const onImport = useCallback(async () => {
    if (rows.length === 0) return;
    setStage("uploading");
    setError(null);
    try {
      const r = await api.lastMileImport(rows);
      setResult(r);
      setStage("done");
    } catch (e) {
      setError(`Error en el import: ${(e as Error).message}`);
      setStage("preview");
    }
  }, [rows]);

  const reset = useCallback(() => {
    setStage("idle");
    setFileName(null);
    setRows([]);
    setStats(null);
    setParseWarnings([]);
    setError(null);
    setResult(null);
  }, []);

  return (
    <div className="p-4 space-y-4 max-w-5xl">
      <header>
        <h1 className="text-xl font-semibold text-ink">Importar planificación (Última Milla)</h1>
        <p className="text-sm text-sub mt-1">
          Subí un Excel <span className="font-mono">Reparto_*.xlsx</span> con el plan del
          día — el sistema crea/actualiza clientes, choferes, vehículos, documentos de
          venta, órdenes y rutas en una sola operación. Idempotente: re-subir el mismo
          archivo no duplica.
        </p>
      </header>

      {error && (
        <div className="text-sm text-state-failed bg-state-failed/10 border border-state-failed/30 rounded p-3">
          {error}
        </div>
      )}

      {stage === "idle" && (
        <DropZone
          onSelect={onSelectFile}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
        />
      )}

      {stage === "parsing" && (
        <Card>
          <div className="py-8 text-center text-sub">Procesando {fileName}…</div>
        </Card>
      )}

      {(stage === "preview" || stage === "uploading") && stats && (
        <PreviewView
          fileName={fileName!}
          stats={stats}
          warnings={parseWarnings}
          uploading={stage === "uploading"}
          onImport={onImport}
          onCancel={reset}
        />
      )}

      {stage === "done" && result && (
        <ResultView result={result} onReset={reset} />
      )}

      <ImportHistory refreshKey={stage} />
    </div>
  );
}

// ---------------- Historial de imports ----------------

/// ImportHistory — lista los imports recientes con botón "Deshacer".
/// Se refresca cuando `refreshKey` cambia (típicamente al cerrar un
/// import exitoso). El backend valida que el caller sea admin; si no,
/// devuelve 403 y la sección no se muestra.
function ImportHistory({ refreshKey }: { refreshKey: string }) {
  const [items, setItems] = useState<LastMileImportSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState<LastMileImportSummary | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listLastMileImports();
      setItems(data);
    } catch (e) {
      // 403 esperable para no-admin — ocultamos la sección.
      const msg = (e as Error).message;
      if (msg.includes("403") || msg.toLowerCase().includes("admin")) {
        setItems(null);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [refreshKey]);

  // No mostramos la sección si no es admin (load falló por 403) o no
  // hay imports todavía.
  if (items === null && !loading && !error) return null;
  if (items !== null && items.length === 0 && !loading) return null;

  return (
    <Card className="mt-6">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-ink">Historial de importaciones</h2>
          <p className="text-xs text-sub mt-0.5">
            Deshacer borra solo rutas y órdenes — clientes, vehículos y choferes
            se preservan.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
          ⟳ Refrescar
        </Button>
      </header>

      {error && (
        <div className="text-xs text-state-failed bg-state-failed/10 border border-state-failed/30 rounded p-2 mb-2">
          {error}
        </div>
      )}

      {items && items.length > 0 && (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-page text-sub border-b border-border">
              <tr className="text-left">
                <Th>Fecha</Th>
                <Th>Importado por</Th>
                <Th className="text-right">Órdenes</Th>
                <Th className="text-right">Rutas</Th>
                <Th className="text-right">Documentos</Th>
                <Th className="text-right">Entregas</Th>
                <th className="px-3 py-2 w-24" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const empty =
                  it.orders_count === 0 &&
                  it.routes_count === 0 &&
                  it.sales_documents_count === 0;
                return (
                  <tr key={it.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 text-ink text-xs">
                      {new Date(it.created_at).toLocaleString("es-PE", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="px-3 py-2 text-ink text-xs">
                      {it.created_by_full_name ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {it.orders_count}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {it.routes_count}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {it.sales_documents_count}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {it.deliveries_count > 0 || it.exceptions_count > 0 ? (
                        <span className="text-state-delayed font-semibold">
                          {it.deliveries_count}
                          {it.exceptions_count > 0 && (
                            <span className="text-state-failed">
                              {" "}/ {it.exceptions_count} ✕
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sub">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setConfirming(it)}
                        disabled={empty}
                        className="text-xs text-state-failed hover:underline disabled:text-sub disabled:no-underline disabled:cursor-not-allowed"
                        title={empty ? "Sin datos para deshacer" : "Deshacer importación"}
                      >
                        Deshacer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirming && (
        <ConfirmDeleteImport
          item={confirming}
          onClose={() => setConfirming(null)}
          onDone={async () => {
            setConfirming(null);
            await load();
          }}
        />
      )}
    </Card>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-3 py-2 font-medium text-[11px] uppercase tracking-wide ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

/// Modal de confirmación destructiva. Si hay deliveries/exceptions (POD
/// real del chofer) avisamos explícitamente — borrarlas también es
/// irreversible. El usuario tiene que tipear "DESHACER" para confirmar
/// cuando hay POD, así no se hace por accidente.
function ConfirmDeleteImport({
  item,
  onClose,
  onDone,
}: {
  item: LastMileImportSummary;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const hasPOD = item.deliveries_count > 0 || item.exceptions_count > 0;
  const canConfirm = !hasPOD || typed.trim().toUpperCase() === "DESHACER";

  async function confirmDelete() {
    setBusy(true);
    setError(null);
    try {
      const res: LastMileImportDeleteResult = await api.deleteLastMileImport(item.id);
      await onDone();
      // eslint-disable-next-line no-alert
      alert(
        `Importación deshecha:\n` +
          `· ${res.OrdersDeleted} órdenes\n` +
          `· ${res.RoutesDeleted} rutas\n` +
          `· ${res.SalesDocumentsDeleted} documentos\n` +
          (res.DeliveriesDeleted > 0
            ? `· ${res.DeliveriesDeleted} entregas confirmadas\n`
            : "") +
          (res.ExceptionsDeleted > 0
            ? `· ${res.ExceptionsDeleted} rechazos\n`
            : ""),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[2000] bg-ink/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-card-hover w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-ink mb-3">
          Deshacer importación
        </h2>
        <p className="text-sm text-ink mb-3">
          Esta acción borrará la planificación creada el{" "}
          <strong>{new Date(item.created_at).toLocaleString("es-PE")}</strong>:
        </p>
        <ul className="text-sm text-ink space-y-1 mb-4 pl-4 list-disc">
          <li>
            {item.orders_count} órdenes y {item.routes_count} rutas
          </li>
          <li>{item.sales_documents_count} documentos de venta</li>
          {hasPOD && (
            <li className="text-state-failed font-medium">
              {item.deliveries_count} entregas confirmadas y{" "}
              {item.exceptions_count} rechazos del chofer (POD)
            </li>
          )}
        </ul>
        <p className="text-xs text-sub mb-4">
          Los <strong>clientes, vehículos y choferes</strong> NO se borran:
          siguen disponibles para futuras importaciones.
        </p>

        {hasPOD && (
          <div className="mb-3">
            <label className="block text-xs text-sub mb-1">
              Hay POD del chofer. Para confirmar, tipeá{" "}
              <span className="font-mono font-semibold text-state-failed">DESHACER</span>:
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full border border-border rounded px-2 py-1.5 text-sm font-mono"
              placeholder="DESHACER"
              autoFocus
            />
          </div>
        )}

        {error && (
          <div className="text-xs text-state-failed bg-state-failed/10 border border-state-failed/30 rounded p-2 mb-3">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={confirmDelete}
            disabled={busy || !canConfirm}
            className="!bg-state-failed hover:!bg-state-failed/90"
          >
            {busy ? "Deshaciendo…" : "Deshacer importación"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Drop zone ----------------

function DropZone({
  onSelect,
  isDragging,
  setIsDragging,
}: {
  onSelect: (file: File) => void;
  isDragging: boolean;
  setIsDragging: (v: boolean) => void;
}) {
  return (
    <label
      htmlFor="reparto-file"
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onSelect(f);
      }}
      className={`block cursor-pointer rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
        isDragging
          ? "border-primary bg-primary/5"
          : "border-border bg-card hover:border-primary/50"
      }`}
    >
      <div className="text-4xl mb-3">📄</div>
      <div className="text-base font-medium text-ink mb-1">
        Arrastrá el Excel acá o hacé click para seleccionar
      </div>
      <div className="text-xs text-sub">
        Formato esperado: <span className="font-mono">Reparto_DDMM.xlsx</span> · acepta .xlsx, .xls
      </div>
      <input
        id="reparto-file"
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelect(f);
        }}
      />
    </label>
  );
}

// ---------------- Preview ----------------

function PreviewView({
  fileName,
  stats,
  warnings,
  uploading,
  onImport,
  onCancel,
}: {
  fileName: string;
  stats: RepartoStats;
  warnings: string[];
  uploading: boolean;
  onImport: () => void;
  onCancel: () => void;
}) {
  // ¿Las fechas de despacho son del día actual? Si no, las rutas que
  // se creen NO aparecerán en "Operación en vivo" (ese dashboard filtra
  // por la fecha del día). Avisamos antes de importar para que el admin
  // no se sorprenda. Tres casos:
  //   - todas = hoy   → sin warning
  //   - mix           → warning ámbar ("solo las de hoy se verán")
  //   - todas != hoy  → warning rojo ("ninguna ruta aparecerá en hoy")
  const dateWarning = computeDateWarning(stats.dispatchDates);

  // ¿Vino el Excel SIN columna PLACA / SECUENCIA? Si stats.uniqueVehicles=0
  // y uniqueDrivers=0 quiere decir que el archivo es "solo órdenes": no se
  // van a crear rutas ni route_stops, y los repartidores tendrán que
  // asignarse cada paquete por sí mismos vía el flujo de scan (Fase 5).
  // El admin debe aceptar explícitamente este modo — flujo bastante
  // distinto del import "completo" típico, no quiero que dispare sin
  // querer.
  const noRoutesDetected =
    stats.uniqueVehicles === 0 && stats.uniqueDrivers === 0;
  const [acceptedNoRoutes, setAcceptedNoRoutes] = useState(false);
  const importDisabled =
    uploading || (noRoutesDetected && !acceptedNoRoutes);

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-sub">Archivo</div>
            <div className="font-mono text-ink">{fileName}</div>
          </div>
          <Button variant="secondary" onClick={onCancel} disabled={uploading}>
            Cambiar archivo
          </Button>
        </div>
      </Card>

      {dateWarning && (
        <div
          className={`border rounded p-3 text-sm ${
            dateWarning.severity === "danger"
              ? "bg-state-failed/10 border-state-failed/40 text-state-failed"
              : "bg-state-delayed/10 border-state-delayed/40 text-state-delayed"
          }`}
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <span>⚠</span>
            <span>{dateWarning.title}</span>
          </div>
          <p className="text-[13px] leading-snug">{dateWarning.message}</p>
          {dateWarning.dates.length > 0 && (
            <p className="text-[12px] mt-1.5 opacity-80">
              Fechas en el archivo:{" "}
              <span className="font-mono">{dateWarning.dates.join(", ")}</span>
            </p>
          )}
        </div>
      )}

      {noRoutesDetected && (
        <NoRoutesNotice
          totalDocs={stats.totalDocs}
          accepted={acceptedNoRoutes}
          onAccept={() => setAcceptedNoRoutes(true)}
        />
      )}

      {/* KPIs principales */}
      <Card>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Documentos" value={stats.totalDocs} />
          <Stat label="Clientes únicos" value={stats.uniqueClients} />
          <Stat label="Choferes" value={stats.uniqueDrivers} />
          <Stat label="Vehículos" value={stats.uniqueVehicles} />
          <Stat
            label="Sin coordenadas"
            value={stats.rowsWithoutCoords}
            accent={stats.rowsWithoutCoords > 0}
          />
        </div>
      </Card>

      {/* Fechas de despacho + estados (lado a lado) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <SectionTitle>Fechas de despacho</SectionTitle>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-sub text-left">
                <th className="py-1 font-medium">Fecha</th>
                <th className="py-1 font-medium text-right">Documentos</th>
              </tr>
            </thead>
            <tbody>
              {stats.dispatchDates.map((d) => (
                <tr key={d.date} className="border-t border-border">
                  <td className="py-1">{d.date}</td>
                  <td className="py-1 text-right tabular-nums">{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card>
          <SectionTitle>Estado de los documentos</SectionTitle>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-sub text-left">
                <th className="py-1 font-medium">Estado</th>
                <th className="py-1 font-medium text-right">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {stats.estadosCount.map((e) => (
                <tr key={e.estado} className="border-t border-border">
                  <td className="py-1">
                    <Badge variant={estadoVariant(e.estado)}>{e.estado}</Badge>
                  </td>
                  <td className="py-1 text-right tabular-nums">{e.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Tabla choferes */}
      <Card padding="none">
        <div className="px-4 pt-3 pb-2 border-b border-border">
          <SectionTitle>Choferes detectados ({stats.drivers.length})</SectionTitle>
        </div>
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-sub text-left sticky top-0 bg-card">
              <tr>
                <th className="px-3 py-2 font-medium">Placa</th>
                <th className="px-3 py-2 font-medium">Transportista</th>
                <th className="px-3 py-2 font-medium text-right">Docs</th>
                <th className="px-3 py-2 font-medium text-right">Clientes</th>
              </tr>
            </thead>
            <tbody>
              {stats.drivers.map((d) => (
                <tr key={d.placa} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-ink">{d.placa}</td>
                  <td className="px-3 py-2 text-sub">{d.transportista}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{d.docs}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{d.clients}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {warnings.length > 0 && (
        <details className="text-xs text-state-delayed">
          <summary className="cursor-pointer">{warnings.length} advertencias del parseo</summary>
          <ul className="list-disc pl-5 mt-1">
            {warnings.slice(0, 20).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
            {warnings.length > 20 && <li>… y {warnings.length - 20} más</li>}
          </ul>
        </details>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onCancel} disabled={uploading}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={onImport} disabled={importDisabled}>
          {uploading
            ? "Importando…"
            : noRoutesDetected
            ? `Cargar ${stats.totalDocs} órdenes (sin rutas)`
            : `Importar ${stats.totalDocs} documentos`}
        </Button>
      </div>
    </div>
  );
}

/// NoRoutesNotice — banner ámbar que aparece cuando el Excel de última
/// milla no trae PLACA ni SECUENCIA. El admin tiene que apretar
/// "Aceptar y cargar solo órdenes" para que el botón principal se
/// habilite. Una vez aceptado, las órdenes se cargan en estado pending
/// para que los repartidores las asignen vía el flujo de barcode scan
/// (mobile / Fase 5).
function NoRoutesNotice({
  totalDocs,
  accepted,
  onAccept,
}: {
  totalDocs: number;
  accepted: boolean;
  onAccept: () => void;
}) {
  return (
    <div className="border rounded p-3 text-sm bg-state-delayed/10 border-state-delayed/40 text-state-delayed">
      <div className="font-semibold mb-1 flex items-center gap-1.5">
        <span>⚠</span>
        <span>El archivo no incluye rutas</span>
      </div>
      <p className="text-[13px] leading-snug">
        No se detectaron columnas PLACA / SECUENCIA. Si confirmás, vamos a
        cargar las {totalDocs} órdenes en estado pendiente; los repartidores
        las asignarán escaneando el código de barras de la factura desde la
        app móvil.
      </p>
      <p className="text-[12px] mt-1.5 opacity-80">
        Si esto es un error y el archivo SÍ debía traer rutas, cancelá y
        revisá las columnas PLACA / SECUENCIA antes de re-subir.
      </p>
      {!accepted && (
        <div className="mt-2">
          <Button variant="secondary" size="sm" onClick={onAccept}>
            Aceptar y cargar solo órdenes
          </Button>
        </div>
      )}
      {accepted && (
        <div className="mt-2 text-[12px] font-semibold flex items-center gap-1.5">
          <span>✓</span>
          <span>Modo "solo órdenes" aceptado. Tocá el botón inferior para confirmar.</span>
        </div>
      )}
    </div>
  );
}

// ---------------- Resultado del import ----------------

function ResultView({ result, onReset }: { result: LastMileImportResult; onReset: () => void }) {
  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-start gap-3">
          <div className="text-3xl">✓</div>
          <div>
            <div className="text-base font-medium text-state-delivered">
              Import completado
            </div>
            <div className="text-sm text-sub">
              {result.RowsSkipped > 0 && (
                <>Se omitieron {result.RowsSkipped} filas con datos insuficientes. </>
              )}
              Verificá los datos en /supervisor o /tracking.
            </div>
          </div>
        </div>
      </Card>

      <Card padding="none">
        <table className="w-full text-sm">
          <thead className="text-sub text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Entidad</th>
              <th className="px-3 py-2 font-medium text-right">Creados</th>
              <th className="px-3 py-2 font-medium text-right">Actualizados</th>
              <th className="px-3 py-2 font-medium text-right">Sin cambios</th>
            </tr>
          </thead>
          <tbody>
            <ResultRow label="Almacenes"           {...result.Warehouses}     />
            <ResultRow label="Clientes"             {...result.Clients}        />
            <ResultRow label="Choferes"             {...result.Drivers}        />
            <ResultRow label="Vehículos"            {...result.Vehicles}       />
            <ResultRow label="Órdenes de entrega"   {...result.Orders}         />
            <ResultRow label="Documentos de venta"  {...result.SalesDocuments} />
            <ResultRow label="Rutas"                {...result.Routes}         />
            <ResultRow label="Paradas"              {...result.RouteStops}     />
          </tbody>
        </table>
      </Card>

      {result.Errors && result.Errors.length > 0 && (
        <details className="text-sm text-state-failed">
          <summary className="cursor-pointer">{result.Errors.length} errores no fatales</summary>
          <ul className="list-disc pl-5 mt-1">
            {result.Errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex justify-end pt-2">
        <Button variant="primary" onClick={onReset}>
          Importar otro
        </Button>
      </div>
    </div>
  );
}

function ResultRow({
  label,
  Created,
  Updated,
  Found,
}: {
  label: string;
  Created: number;
  Updated?: number;
  Found?: number;
}) {
  return (
    <tr className="border-t border-border">
      <td className="px-3 py-2 text-ink">{label}</td>
      <td className="px-3 py-2 text-right tabular-nums text-state-delivered">
        {Created || "—"}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{Updated ?? "—"}</td>
      <td className="px-3 py-2 text-right tabular-nums text-sub">{Found ?? "—"}</td>
    </tr>
  );
}

// ---------------- helpers presentacionales ----------------

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wide text-sub">{label}</div>
      <div className={`text-2xl font-semibold font-mono ${accent ? "text-state-failed" : "text-ink"}`}>
        {value.toLocaleString("es-PE")}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs uppercase tracking-wide text-sub font-medium mb-2">{children}</h3>;
}

function estadoVariant(estado: string): Parameters<typeof Badge>[0]["variant"] {
  const e = estado.toUpperCase();
  if (e.includes("DESPACHADO") || e.includes("ENTREGADO")) return "delivered";
  if (e.includes("DEV") && e.includes("PARCIAL")) return "delayed";
  if (e.includes("DEV")) return "failed";
  return "neutral";
}

function describeParseError(err: ParseError): string {
  switch (err.kind) {
    case "no-sheet":
      return "El Excel no tiene ninguna hoja.";
    case "no-data":
      return "El Excel no tiene filas con datos.";
    case "missing-headers":
      return `Faltan columnas requeridas en el Excel: ${err.missing.join(", ")}.`;
  }
}

/// Inspecciona las fechas de despacho del Excel parseado y devuelve
/// un warning si NO coinciden con el día de hoy. El sistema crea las
/// rutas con `planned_date = dispatch_date`, así que si el Excel es de
/// otra fecha las rutas existen pero quedan "en historial" — no se ven
/// en Operación en vivo (que filtra hoy).
type DateWarning = {
  severity: "danger" | "warn";
  title: string;
  message: string;
  /** Fechas únicas del archivo (ya formateadas dd/mm o ISO). */
  dates: string[];
};

function computeDateWarning(
  dispatchDates: { date: string; count: number }[],
): DateWarning | null {
  if (dispatchDates.length === 0) return null;
  const today = todayLocalISO();
  const realDates = dispatchDates.filter((d) => d.date !== "(sin fecha)");
  if (realDates.length === 0) return null;

  const hasToday = realDates.some((d) => d.date === today);
  const hasOther = realDates.some((d) => d.date !== today);

  if (hasToday && !hasOther) return null; // todo es hoy, todo bien

  const otherFormatted = realDates
    .filter((d) => d.date !== today)
    .map((d) => formatDateHuman(d.date));

  if (hasToday && hasOther) {
    return {
      severity: "warn",
      title: "Mezcla de fechas en el archivo",
      message:
        "El archivo tiene órdenes para hoy y para otras fechas. " +
        "Solo las rutas con fecha de despacho de HOY aparecerán en " +
        '"Operación en vivo". Las demás quedan creadas pero invisibles ' +
        "hasta que selecciones esa fecha en Supervisión/Tracking.",
      dates: otherFormatted,
    };
  }

  return {
    severity: "danger",
    title: "El archivo NO es del día de hoy",
    message:
      "Se cargarán clientes, órdenes, vehículos y almacenes como siempre, " +
      "pero las rutas generadas quedan asociadas a la fecha del archivo. " +
      'NO van a aparecer en "Operación en vivo" hoy. Para verlas, abrí ' +
      "Supervisión/Tracking y cambiá la fecha al día del archivo.",
    dates: otherFormatted,
  };
}

/// Devuelve YYYY-MM-DD en hora local — no UTC. El Excel suele venir
/// con fechas locales y comparar contra `toISOString().slice(0,10)`
/// puede dar un día de diferencia a la tarde-noche.
function todayLocalISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/// Formatea "2026-06-08" → "domingo 8 de junio".
function formatDateHuman(iso: string): string {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("es-PE", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch {
    return iso;
  }
}
